import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CriterionInput,
  RubricCriterionRead,
  RubricPayload,
  createRubric,
  getRubric,
  saveCriteria,
  updateRubric,
} from "../services/rubricAdminService";
import { Framework, listFrameworks } from "../services/frameworkAdminService";
import {
  listScenarioOptions,
  ScenarioOption,
} from "../services/scenarioAdminService";
import FrameworkPicker from "./FrameworkPicker";
import AllowedScenariosMultiselect from "./AllowedScenariosMultiselect";
import CriteriaEditor from "./rubric-editor/CriteriaEditor";
import { usePermissionLevel } from "../hooks/usePermissionLevel";

// Map the GET /rubrics/:id read shape (examples are a flat sibling array on
// each criterion, tagged with performanceLevelId) into the nested editor input
// shape (examples nested under their performance level).
function toEditorTree(criteria: RubricCriterionRead[]): CriterionInput[] {
  return (criteria ?? []).map((c) => ({
    id: c.id,
    kind: c.kind,
    name: c.name,
    description: c.description,
    instructions: c.instructions,
    weight: c.weight,
    performanceLevels: (c.performanceLevels ?? []).map((l) => ({
      id: l.id,
      name: l.name,
      minGrade: l.minGrade,
      maxGrade: l.maxGrade,
      description: l.description,
      instructions: l.instructions ?? undefined,
      examples: (c.examples ?? [])
        .filter((e) => e.performanceLevelId === l.id)
        .map((e) => ({
          id: e.id,
          conversationText: e.conversationText ?? undefined,
          solution: e.solution
            ? {
                answer: e.solution.answer ?? undefined,
                artifacts: e.solution.artifacts ?? [],
              }
            : undefined,
        })),
    })),
  }));
}

interface RubricEditorPageProps {
  getToken: () => string | undefined;
}

const RubricEditorPage: React.FC<RubricEditorPageProps> = ({ getToken }) => {
  const navigate = useNavigate();
  const { orgSlug, rubricId } = useParams();
  const { isAdmin } = usePermissionLevel();
  const isEdit = Boolean(rubricId);

  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [scenarioOptions, setScenarioOptions] = useState<ScenarioOption[]>([]);
  const [form, setForm] = useState<RubricPayload>({
    name: "",
    frameworkId: "",
    allowedScenarios: [],
    conversationInstructions: "",
    solutionInstructions: "",
  });
  const [isGlobal, setIsGlobal] = useState(false);
  const [criteria, setCriteria] = useState<CriterionInput[]>([]);
  const [rubricIdState, setRubricIdState] = useState<string | undefined>(rubricId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const loadAll = useCallback(async () => {
    const token = getToken();
    try {
      const [fw, opts] = await Promise.all([listFrameworks(token), listScenarioOptions(token)]);
      setFrameworks(fw);
      setScenarioOptions(opts);
      if (isEdit && rubricId) {
        const r = await getRubric(rubricId, token);
        setForm({
          name: r.name,
          frameworkId: r.framework.id,
          allowedScenarios: r.allowedScenarios,
          conversationInstructions: r.conversationInstructions ?? "",
          solutionInstructions: r.solutionInstructions ?? "",
        });
        setCriteria(toEditorTree(r.criteria));
        setIsGlobal(r.isGlobal);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [getToken, isEdit, rubricId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      let id = rubricIdState;
      if (isEdit && id) {
        // Only an admin may set this — see the matching guard in
        // ScenarioCreatePage. The checkbox is already admin-gated; this re-check
        // keeps a stale value out of a manager's payload.
        await updateRubric(id, isAdmin ? { ...form, isGlobal } : form, token);
      } else {
        const created = await createRubric(form, token);
        id = created.id;
        setRubricIdState(id);
      }
      await saveCriteria(id!, criteria, token);
      // On create, land on the new rubric's editor (id now known) so criteria
      // can be authored in place; on edit, return to the library.
      navigate(isEdit ? `/${orgSlug}/rubrics` : `/${orgSlug}/rubrics/${id}/edit`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      setSaving(false);
    }
  };

  const set = <K extends keyof RubricPayload>(k: K, v: RubricPayload[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-2xl w-full mx-auto px-8 py-10 text-on-surface">
        <button type="button" className="text-slate-400 text-sm mb-4" onClick={() => navigate(`/${orgSlug}/rubrics`)}>
          ‹ Back to Rubrics
        </button>
        <h1 className="text-xl font-bold mb-4">{isEdit ? "Edit Rubric" : "New Rubric"}</h1>
        {error && <div className="mb-3 text-error text-sm">{error}</div>}

        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : (
          <>
            <div className="mb-4">
              <label className="form-label">Name</label>
              <input className="form-control" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>

            <div className="mb-4">
              <label className="form-label">Framework</label>
              <FrameworkPicker
                frameworks={frameworks}
                value={form.frameworkId}
                onChange={(id) => set("frameworkId", id)}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Allowed scenarios (eligibility)</label>
              <AllowedScenariosMultiselect
                options={scenarioOptions}
                selected={form.allowedScenarios}
                onChange={(ids) => set("allowedScenarios", ids)}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Conversation instructions</label>
              <textarea
                className="form-control h-24"
                value={form.conversationInstructions}
                onChange={(e) => set("conversationInstructions", e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Solution instructions</label>
              <textarea
                className="form-control h-24"
                value={form.solutionInstructions}
                onChange={(e) => set("solutionInstructions", e.target.value)}
              />
            </div>

            {rubricIdState ? (
              <CriteriaEditor
                rubricId={rubricIdState}
                getToken={getToken}
                criteria={criteria}
                onChange={setCriteria}
              />
            ) : (
              <div className="border-t border-outline-variant/30 mt-6 pt-4 text-slate-400 text-sm">
                Save this rubric once to start adding criteria, performance levels, and examples.
              </div>
            )}

            {isEdit && isAdmin && (
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  disabled={saving}
                  className="h-4 w-4 rounded border-outline-variant/40 bg-surface-container-low"
                />
                <span className="text-xs font-label uppercase tracking-wider text-slate-400">
                  Shared catalog — visible to every organization
                </span>
              </label>
            )}

            <div className="flex gap-3 mt-4">
              <button
                type="button"
                className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                disabled={saving || !form.name.trim() || !form.frameworkId}
                onClick={save}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm text-slate-300"
                onClick={() => navigate(`/${orgSlug}/rubrics`)}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RubricEditorPage;
